import { PracticeQueryParams } from 'constants/application-constants'

import { useCallback, useEffect } from 'react'

import { useAppDispatch, useAppSelector } from 'hooks'
import { useSearchParams } from 'react-router-dom'
import { AvailableRevisions, Revision } from 'redux-store/models'

import {
  setDocuments,
  setMainDocument,
} from '../../Documents/api/currentDocumentsSlice'
import {
  practicesApi,
  setCurrentRevisionLoading,
  setCurrentRevision,
  selectCurrentRevision,
  setAvailableRevisions,
} from '../api'

/**
 *
 * Hook used for setting the revision status in query params according to view type(front-office/back-office) and practice ID
 *
 * @param isFrontOffice - {Boolean} - is the current view front office?
 * @param practiceId - {string} - id of the practice for which the status needs to be set
 * @returns current revision
 */
export const useParamRevisionStatus = (
  isFrontOffice: boolean,
  practiceId: string
) => {
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const currentRevision = useAppSelector(selectCurrentRevision)
  const httpGetPractice = practicesApi.useGetPracticeByIdQuery(practiceId, {
    skip: !practiceId,
  })
  const [httpGetRevision] = practicesApi.useLazyGetRevisionByStatusQuery()

  const setRevisionParam = useCallback(
    (param: 'published' | 'draft' | 'approval') => {
      if (param) {
        searchParams.set(PracticeQueryParams.REVISION_STATUS, param)
      } else {
        searchParams.delete(PracticeQueryParams.REVISION_STATUS)
      }
      setSearchParams(searchParams, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  const fetchRevision = useCallback(
    async (param: 'published' | 'draft' | 'approval') => {
      dispatch(setCurrentRevisionLoading(true))
      try {
        const resp = await httpGetRevision({
          id: practiceId,
          status: param,
        }).unwrap()
        if (!resp || !resp.hasOwnProperty('data')) {
          throw Error('no revision data')
        }
        dispatch(setCurrentRevision(resp.data))
        dispatch(setMainDocument(resp.data.main_document[0]))
        dispatch(setDocuments(resp.data.documents))
        dispatch(setCurrentRevisionLoading(false))
      } catch (err: any) {
        dispatch(setCurrentRevisionLoading(false))
        if (param === 'approval') {
          dispatch(
            setAvailableRevisions({
              approval: false,
            } as AvailableRevisions)
          )
        }
        setRevisionParam('draft')
      }
    },
    [dispatch, httpGetRevision, practiceId, setRevisionParam]
  )

  useEffect(() => {
    if (!httpGetPractice.isSuccess) return

    const paramRevision = searchParams.get(PracticeQueryParams.REVISION_STATUS)

    // set revision to draft in Back-office if no parameter is set
    if (!paramRevision) {
      setRevisionParam('draft')
      return
    }

    if (currentRevision && paramRevision !== currentRevision?.status) {
      // exception for preapproval as params do not handle it
      if (
        paramRevision === 'approval' &&
        currentRevision?.status === 'preapproval'
      ) {
        return
      }

      fetchRevision(paramRevision as any)
    }
  }, [
    dispatch,
    httpGetPractice.isSuccess,
    searchParams,
    setRevisionParam,
    currentRevision,
    fetchRevision,
  ])

  // set initial revision & available revisions based on guideline
  useEffect(() => {
    let revision: null | Revision = null

    if (httpGetPractice.isSuccess) {
      revision = httpGetPractice.data.data.current_draftish_revision
      if (httpGetPractice.data?.data?.available_revisions) {
        dispatch(
          setAvailableRevisions(httpGetPractice.data.data.available_revisions)
        )
      }
    }

    dispatch(setCurrentRevision(revision))

    return () => {
      dispatch(setCurrentRevision(null))
    }
  }, [dispatch, httpGetPractice.data, httpGetPractice.isSuccess])
}
